import React from 'react';
import { Link } from '../router/Link';

const STEPS = [
  {
    title: '삭제 요청 이메일 발송',
    desc: '아래 버튼을 눌러 가입 이메일과 함께 요청을 보내주세요.',
  },
  {
    title: '신원 확인',
    desc: '가입 시 사용한 이메일로 확인 요청을 드립니다.',
  },
  {
    title: '삭제 처리',
    desc: '확인 완료 후 영업일 기준 7일 이내에 처리합니다.',
  },
  {
    title: '완료 통보',
    desc: '삭제가 완료되면 이메일로 안내드립니다.',
  },
];

const DATA_ITEMS = [
  '계정 정보 (이메일, 프로필)',
  '로그인 기록 및 세션 데이터',
  '작성한 댓글 · 게시물',
  '앱 내 환경설정 및 즐겨찾기',
  '위치정보 이용 기록 (해당하는 경우)',
];

const DeleteAccountPage: React.FC = () => {
  const cardStyle: React.CSSProperties = {
    background: 'var(--card)',
    border: '1px solid var(--stroke)',
    borderRadius: 'var(--radius)',
    padding: '1.75rem',
    marginBottom: '1.25rem',
  };

  const h2Style: React.CSSProperties = {
    fontFamily: 'var(--font-body)',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.15em',
    textTransform: 'uppercase' as const,
    color: 'var(--muted)',
    marginBottom: '1rem',
  };

  return (
    <main
      style={{
        maxWidth: '52rem',
        margin: '0 auto',
        padding: '3rem 1.5rem 6rem',
      }}
    >
      {/* Header */}
      <header style={{ marginBottom: '2.5rem' }}>
        <p
          style={{
            fontSize: '0.65rem',
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'var(--muted)',
            margin: '0 0 0.5rem',
          }}
        >
          Noraneko · Privacy
        </p>
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(2rem, 6vw, 3.5rem)',
            letterSpacing: '0.04em',
            margin: '0 0 0.5rem',
            color: 'var(--text)',
          }}
        >
          계정 및 데이터 삭제
        </h1>
        <p style={{ fontSize: '0.875rem', color: 'var(--muted)', margin: 0 }}>
          개인정보 보호법 및 앱스토어 정책에 따라 계정과 관련 데이터의 삭제를 요청할 수 있습니다.
        </p>
      </header>

      {/* 삭제 대상 데이터 */}
      <div style={cardStyle}>
        <h2 style={h2Style}>삭제되는 데이터</h2>
        <ul
          style={{
            margin: 0,
            paddingLeft: '1.25rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.35rem',
          }}
        >
          {DATA_ITEMS.map((item) => (
            <li key={item} style={{ fontSize: '0.875rem', color: 'var(--text)', lineHeight: 1.6 }}>
              {item}
            </li>
          ))}
        </ul>
        <p
          style={{
            marginTop: '1rem',
            fontSize: '0.775rem',
            color: 'var(--muted)',
            lineHeight: 1.65,
            borderTop: '1px solid var(--stroke)',
            paddingTop: '0.875rem',
          }}
        >
          단, 법령에 따라 일정 기간 보관이 의무인 데이터(결제 기록, 분쟁 기록 등)는 해당 보관 기간 종료 후 삭제됩니다.
        </p>
      </div>

      {/* 처리 절차 */}
      <div style={cardStyle}>
        <h2 style={h2Style}>처리 절차</h2>
        <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {STEPS.map((step, i) => (
            <li
              key={step.title}
              style={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'flex-start',
                padding: '0.875rem 0',
                borderBottom: i < STEPS.length - 1 ? '1px solid var(--stroke-subtle)' : 'none',
              }}
            >
              <span
                aria-hidden
                style={{
                  flexShrink: 0,
                  width: '1.75rem',
                  height: '1.75rem',
                  borderRadius: '50%',
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--stroke)',
                  color: 'var(--primary)',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: '0.1rem',
                }}
              >
                {i + 1}
              </span>
              <div>
                <p style={{ margin: '0 0 0.15rem', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)' }}>
                  {step.title}
                </p>
                <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--muted)', lineHeight: 1.6 }}>
                  {step.desc}
                </p>
              </div>
            </li>
          ))}
        </ol>

        {/* CTA */}
        <div style={{ marginTop: '1.5rem' }}>
          <a
            href={`mailto:support@noraneko.cc?subject=${encodeURIComponent('계정 및 데이터 삭제 요청')}&body=${encodeURIComponent('안녕하세요,\n\n아래 정보로 계정 및 데이터 삭제를 요청합니다.\n\n가입 이메일: \n삭제 요청 사유: \n')}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.65rem 1.4rem',
              background: 'var(--primary)',
              color: '#fff',
              fontSize: '0.8rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              borderRadius: '8px',
              textDecoration: 'none',
              transition: 'opacity 0.15s',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = '0.85'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = '1'; }}
          >
            ✉ 삭제 요청 이메일 보내기
          </a>
          <p style={{ margin: '0.75rem 0 0', fontSize: '0.75rem', color: 'var(--muted)', lineHeight: 1.65 }}>
            이메일에 <strong>가입 시 사용한 이메일 주소</strong>를 반드시 포함해주세요.
          </p>
        </div>
      </div>

      {/* 기타 문의 */}
      <div style={cardStyle}>
        <h2 style={h2Style}>기타 개인정보 문의</h2>
        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text)', lineHeight: 1.7 }}>
          삭제 이외의 개인정보 관련 문의는{' '}
          <a
            href="mailto:support@noraneko.cc"
            style={{ color: 'var(--accent-blue)', textDecoration: 'underline' }}
          >
            support@noraneko.cc
          </a>
          으로 연락주세요.
          <br />
          <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
            개인정보처리방침은{' '}
            <Link
              to="/legal?doc=privacy"
              style={{ color: 'var(--accent-blue)', textDecoration: 'underline' }}
            >
              이곳
            </Link>
            에서 확인할 수 있습니다.
          </span>
        </p>
      </div>
    </main>
  );
};

export default DeleteAccountPage;
